/**
 * VoiceFeaturesTab — CMS editor for the landing-page Voice Features section.
 * Edits content.voice_features_header + content.voice_features (array of scene cards).
 * On first visit when no scenes exist, automatically loads the 4 defaults so the admin
 * can edit them in place.
 */
import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, ChevronUp, ChevronDown, Mic, RotateCcw } from 'lucide-react';
import { DEFAULT_VOICE_SCENES, DEFAULT_VOICE_HEADER } from '../landing/VoiceFeaturesSection';

const ICON_OPTIONS = [
  { value: 'trophy',     label: '🏆 Trophy (Match Day)' },
  { value: 'headphones', label: '🎧 Headphones (Journal)' },
  { value: 'sparkles',   label: '✨ Sparkles (AI Plan)' },
  { value: 'mic',        label: '🎤 Mic (Schedule)' },
  { value: 'zap',        label: '⚡ Zap (Speed)' },
  { value: 'list',       label: '📋 List (Notes)' },
];

const ACCENT_OPTIONS = [
  { value: 'emerald', label: 'Emerald (brand green)' },
  { value: 'blue',    label: 'Blue' },
  { value: 'amber',   label: 'Amber (orange)' },
  { value: 'rose',    label: 'Rose (red)' },
];

const BADGE_COLOR_OPTIONS = [
  { value: 'emerald', label: 'Emerald' },
  { value: 'blue',    label: 'Blue' },
  { value: 'amber',   label: 'Amber' },
  { value: 'rose',    label: 'Rose' },
];

// Deep clone for safe defaults (so edits don't mutate the shared array)
const cloneDefaults = () => JSON.parse(JSON.stringify(DEFAULT_VOICE_SCENES));

const blankScene = () => ({
  id: `voice_${Date.now()}`,
  title: 'New Voice Feature',
  tagline: 'Short benefit-led tagline.',
  transcript: 'What the coach says (example dictation that the demo will animate).',
  badge: 'NEW',
  badgeColor: 'emerald',
  chips: ['chip 1', 'chip 2'],
  icon: 'mic',
  accent: 'emerald',
});

export function VoiceFeaturesTab({ content, setContent }) {
  const header = { ...DEFAULT_VOICE_HEADER, ...(content.voice_features_header || {}) };
  const scenes = Array.isArray(content.voice_features) ? content.voice_features : [];

  // Auto-populate the 4 defaults the first time the admin opens this tab so they can edit them.
  useEffect(() => {
    if (!Array.isArray(content.voice_features) || content.voice_features.length === 0) {
      setContent(prev => ({ ...prev, voice_features: cloneDefaults() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateHeader = (field, value) => {
    setContent(prev => ({
      ...prev,
      voice_features_header: { ...DEFAULT_VOICE_HEADER, ...(prev.voice_features_header || {}), [field]: value },
    }));
  };

  const updateScene = (index, field, value) => {
    setContent(prev => {
      const list = [...(prev.voice_features || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, voice_features: list };
    });
  };

  const updateSceneChips = (index, chipString) => {
    const chips = chipString.split(',').map(c => c.trim()).filter(Boolean);
    updateScene(index, 'chips', chips);
  };

  const addScene = () => {
    setContent(prev => ({
      ...prev,
      voice_features: [...(prev.voice_features || []), blankScene()],
    }));
  };

  const removeScene = (index) => {
    if (!window.confirm('Remove this scene from the landing page?')) return;
    setContent(prev => ({
      ...prev,
      voice_features: (prev.voice_features || []).filter((_, i) => i !== index),
    }));
  };

  const moveScene = (index, direction) => {
    setContent(prev => {
      const list = [...(prev.voice_features || [])];
      const target = index + direction;
      if (target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...prev, voice_features: list };
    });
  };

  const restoreOneDefault = (defaultScene) => {
    if (!window.confirm(`Add the "${defaultScene.title}" default scene back into the list?`)) return;
    setContent(prev => ({
      ...prev,
      voice_features: [...(prev.voice_features || []), JSON.parse(JSON.stringify(defaultScene))],
    }));
  };

  const resetToDefaults = () => {
    if (!window.confirm('Reset all Voice Features content to the default 4 scenes? Your custom edits in this section will be lost.')) return;
    setContent(prev => ({
      ...prev,
      voice_features_header: undefined,
      voice_features: cloneDefaults(),
    }));
  };

  // Surface the defaults that are NOT currently in the scenes list (so admin can re-add removed ones)
  const sceneIds = new Set(scenes.map(s => s.id));
  const missingDefaults = DEFAULT_VOICE_SCENES.filter(d => !sceneIds.has(d.id));

  return (
    <div className="space-y-6" data-testid="cms-voice-features-tab">
      {/* Section header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Mic className="w-5 h-5 text-emerald-600" />Voice Features Section</CardTitle>
          <CardDescription>
            Edit the landing page section that showcases the voice features. The animated demo auto-cycles through each scene.
            Leave fields blank to use the defaults — clear them entirely below by pressing "Reset to defaults".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Section Badge (small all-caps pill)</Label>
            <Input
              value={header.badge}
              onChange={(e) => updateHeader('badge', e.target.value)}
              placeholder="BUILT FOR COACHES IN MOTION"
              data-testid="cms-voice-header-badge"
            />
          </div>
          <div>
            <Label>Headline</Label>
            <Input
              value={header.title}
              onChange={(e) => updateHeader('title', e.target.value)}
              placeholder="Hands-free. Touchline-ready."
              data-testid="cms-voice-header-title"
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Textarea
              rows={2}
              value={header.subtitle}
              onChange={(e) => updateHeader('subtitle', e.target.value)}
              placeholder="Four voice features so you can keep your eyes on the kids..."
              data-testid="cms-voice-header-subtitle"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Trust line (next to shield icon)</Label>
              <Input
                value={header.trust_line}
                onChange={(e) => updateHeader('trust_line', e.target.value)}
                placeholder="Private & secure. Your voice never leaves your team's data."
                data-testid="cms-voice-header-trust"
              />
            </div>
            <div>
              <Label>Footer line</Label>
              <Input
                value={header.footer_line}
                onChange={(e) => updateHeader('footer_line', e.target.value)}
                placeholder="Free trial includes all voice features."
                data-testid="cms-voice-header-footer"
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Switch
                checked={!header.animation_disabled}
                onCheckedChange={(v) => updateHeader('animation_disabled', !v)}
                data-testid="cms-voice-header-animation"
              />
              <Label className="text-sm">Enable slide-in animation on scroll</Label>
            </div>
            <Button variant="outline" size="sm" onClick={resetToDefaults} data-testid="cms-voice-reset-btn">
              Reset to defaults
            </Button>
          </div>

          {/* Accent palette per theme */}
          <div className="pt-4 border-t border-gray-100 space-y-3">
            <div>
              <Label className="text-sm">Accent colour per theme</Label>
              <p className="text-xs text-gray-500 mt-0.5">
                Swap the default emerald accents (icons, dots, mic glow, badges) for the Grassroots brand green.
                Pick independently for light and dark themes. Scenes with custom accents (blue / amber / rose) are not affected.
              </p>
            </div>
            {[
              { key: 'accent_palette_light', label: 'Light theme' },
              { key: 'accent_palette_dark',  label: 'Dark theme'  },
            ].map(({ key, label }) => {
              const current = header[key] || 'emerald';
              return (
                <div key={key} className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-gray-700 w-24 shrink-0">{label}</span>
                  {[
                    { value: 'emerald', label: 'Emerald (default)', swatch: '#10b981' },
                    { value: 'brand',   label: 'Brand green',       swatch: '#007a33' },
                  ].map(opt => {
                    const selected = current === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => updateHeader(key, opt.value)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                          selected ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                        data-testid={`cms-voice-${key}-${opt.value}`}
                      >
                        <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ backgroundColor: opt.swatch }} />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Phone frame dimensions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Phone Frame Dimensions</CardTitle>
          <CardDescription>
            Override the animated demo phone's width and minimum height. Leave blank (or click Reset) to keep the demo
            fully responsive (defaults: 360px wide, 560px tall minimum).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Width (px)</Label>
              <Input
                type="number"
                min="240"
                max="640"
                placeholder="360"
                value={header.phone_width_px ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateHeader('phone_width_px', v === '' ? undefined : Number(v));
                }}
                data-testid="cms-voice-phone-width"
              />
            </div>
            <div>
              <Label className="text-sm">Minimum height (px)</Label>
              <Input
                type="number"
                min="360"
                max="900"
                placeholder="560"
                value={header.phone_min_height_px ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateHeader('phone_min_height_px', v === '' ? undefined : Number(v));
                }}
                data-testid="cms-voice-phone-height"
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-gray-500">
              Tip: keep width between 280–420px for the most natural mobile look. Larger values stretch the phone outside its column on desktop.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                updateHeader('phone_width_px', undefined);
                updateHeader('phone_min_height_px', undefined);
              }}
              data-testid="cms-voice-phone-reset"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset to responsive
            </Button>
          </div>

          {/* Bezel colour */}
          <div className="pt-4 border-t border-gray-100 space-y-3">
            <div>
              <Label className="text-sm">Bezel colour</Label>
              <p className="text-xs text-gray-500 mt-0.5">Pick a preset or enter a custom hex. Leave blank for the default near-black bezel.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { value: '#0a0a0a', label: 'Black' },
                { value: '#9ca3af', label: 'Silver' },
                { value: '#007a33', label: 'Brand green' },
                { value: '#1d4ed8', label: 'Blue' },
                { value: '#f59e0b', label: 'Amber' },
              ].map(opt => {
                const selected = (header.phone_bezel_color || '').toLowerCase() === opt.value.toLowerCase();
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateHeader('phone_bezel_color', opt.value)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                      selected ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                    data-testid={`cms-voice-bezel-preset-${opt.value.replace('#', '')}`}
                  >
                    <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: opt.value }} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-xs text-gray-500">Custom hex:</Label>
              <input
                type="color"
                value={header.phone_bezel_color || '#0a0a0a'}
                onChange={(e) => updateHeader('phone_bezel_color', e.target.value)}
                className="h-8 w-12 rounded border border-gray-200 cursor-pointer"
                data-testid="cms-voice-bezel-colorpicker"
                aria-label="Phone bezel colour picker"
              />
              <Input
                type="text"
                placeholder="#0a0a0a"
                value={header.phone_bezel_color || ''}
                onChange={(e) => updateHeader('phone_bezel_color', e.target.value || undefined)}
                className="w-32 h-8 text-xs"
                data-testid="cms-voice-bezel-hex"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateHeader('phone_bezel_color', undefined)}
                data-testid="cms-voice-bezel-reset"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset
              </Button>
            </div>
          </div>

          {/* Status bar time */}
          <div className="pt-4 border-t border-gray-100 space-y-3">
            <div>
              <Label className="text-sm">Status bar time</Label>
              <p className="text-xs text-gray-500 mt-0.5">Choose what the phone's status bar clock displays.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { value: 'apple',  label: 'Apple classic (9:41)' },
                { value: 'live',   label: 'Live time (visitor\'s clock)' },
                { value: 'custom', label: 'Custom' },
              ].map(opt => {
                const current = header.phone_time_mode || 'apple';
                const selected = current === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateHeader('phone_time_mode', opt.value)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                      selected ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                    data-testid={`cms-voice-time-mode-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {(header.phone_time_mode || 'apple') === 'custom' && (
              <div className="flex items-center gap-3 flex-wrap">
                <Label className="text-xs text-gray-500 shrink-0">Custom text:</Label>
                <Input
                  type="text"
                  placeholder="15:00"
                  maxLength={8}
                  value={header.phone_time_custom || ''}
                  onChange={(e) => updateHeader('phone_time_custom', e.target.value || undefined)}
                  className="w-32 h-8 text-xs"
                  data-testid="cms-voice-time-custom"
                />
                <span className="text-xs text-gray-400">e.g. 15:00, "Match Day", 21:47</span>
              </div>
            )}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  updateHeader('phone_time_mode', undefined);
                  updateHeader('phone_time_custom', undefined);
                }}
                data-testid="cms-voice-time-reset"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset to Apple classic
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scenes */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Scenes ({scenes.length || 'using defaults'})</CardTitle>
            <CardDescription>
              Each scene is one card in the auto-cycling demo + one feature pillar. Leave the scene list empty to use the 4 defaults
              (Match Day Live → Voice Coach Journal → AI Training Plan → Schedule by Voice).
            </CardDescription>
          </div>
          <Button onClick={addScene} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cms-voice-add-scene-btn">
            <Plus className="w-4 h-4 mr-1" />Add scene
          </Button>
        </CardHeader>
        <CardContent>
          {scenes.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500" data-testid="cms-voice-no-scenes-state">
              No custom scenes yet — the landing page is using the default 4 (Match Day Live, Voice Coach Journal, AI Training Plan, Schedule by Voice).
              Click "Add scene" to start customising.
            </div>
          ) : (
            <div className="space-y-4">
              {scenes.map((scene, idx) => (
                <Card key={scene.id || idx} className="border-gray-200" data-testid={`cms-voice-scene-${idx}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-gray-900">
                        Scene #{idx + 1} {scene.title ? `— ${scene.title}` : ''}
                      </p>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => moveScene(idx, -1)} disabled={idx === 0} data-testid={`cms-voice-scene-up-${idx}`}>
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => moveScene(idx, 1)} disabled={idx === scenes.length - 1} data-testid={`cms-voice-scene-down-${idx}`}>
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeScene(idx)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`cms-voice-scene-delete-${idx}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Title</Label>
                        <Input value={scene.title || ''} onChange={(e) => updateScene(idx, 'title', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Tagline (short benefit)</Label>
                        <Input value={scene.tagline || ''} onChange={(e) => updateScene(idx, 'tagline', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Example transcript (what the coach says)</Label>
                      <Textarea rows={2} value={scene.transcript || ''} onChange={(e) => updateScene(idx, 'transcript', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Result badge (e.g. EVENT LOGGED)</Label>
                        <Input value={scene.badge || ''} onChange={(e) => updateScene(idx, 'badge', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Badge colour</Label>
                        <Select value={scene.badgeColor || 'emerald'} onValueChange={(v) => updateScene(idx, 'badgeColor', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {BADGE_COLOR_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Accent colour</Label>
                        <Select value={scene.accent || 'emerald'} onValueChange={(v) => updateScene(idx, 'accent', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ACCENT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Icon</Label>
                        <Select value={scene.icon || 'mic'} onValueChange={(v) => updateScene(idx, 'icon', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ICON_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Result chips (comma-separated)</Label>
                        <Input
                          value={(scene.chips || []).join(', ')}
                          onChange={(e) => updateSceneChips(idx, e.target.value)}
                          placeholder="Goal · Jamie, Assist · Sam, 23'"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default VoiceFeaturesTab;
